/**
 * Created by Daniil Vat. on 13/4/2021
 * Copyright © 2021 Curriculum Ltd. All rights reserved.
 */

import { Component, Input, OnInit } from '@angular/core';
import { ChartConfiguration, ChartType, Chart, Plugin } from 'chart.js';
import * as pluginDataLabels from 'chartjs-plugin-datalabels';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'curr-assessment-results-chart',
  templateUrl: './assessment-results-chart.component.html',
  styleUrls: ['./assessment-results-chart.component.scss']
})
export class AssessmentResultsChartComponent implements OnInit {
  // Constants for chart labels to ensure consistency
  private readonly CUMULATIVE_TEST_RESULTS_KEY = 'shared.chart.cumulativeTestResults';
  private readonly BENCHMARK_KEY = 'shared.chart.benchmark';

  private cumulativeTestResultsLabel: string;
  private benchmarkLabel: string;

  // initialize to avoid undefined
  private labels: string[] = [];

  @Input() set data(chartData: { grades: any[]; averageBenchmark: string[] }) {
    if (chartData && chartData.grades) {
      const hasItems = chartData.grades.filter(item => !!item);
      this.isChartBlur = hasItems.length === 0;

      // Make sure translated labels are initialized
      if (!this.cumulativeTestResultsLabel || !this.benchmarkLabel) {
        this.updateTranslatedLabels();
      }

      if (!isNaN(Number(chartData.averageBenchmark[0]))) {
        this.labels = ['', '9', '8', '7', '6', '5', '4', '3', '2', '1'];
      } else {
        this.labels = ['', 'A*', 'A', 'B', 'C', 'D', 'E', 'U (NP)', 'U (BS)', 'U (Fail)'];
      }

      this.lineChartData = [
        {
          data: chartData.grades.map(value => value === null ? undefined : value),
          label: this.cumulativeTestResultsLabel,
          fill: false,
          tension: 0.3,
          spanGaps: true,
          borderColor: '#006878',
          backgroundColor: '#006878',
          pointBackgroundColor: '#006878',
          pointBorderColor: '#006878',
          pointHoverBackgroundColor: '#006878',
          pointHoverBorderColor: '#006878',
          borderWidth: 3,
        },
        {
          data: chartData.averageBenchmark,
          label: this.benchmarkLabel,
          fill: false,
          tension: 0,
          borderColor: '#5DB605',
          backgroundColor: '#5DB605',
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 0,
          hoverBorderWidth: 0,
          hitRadius: 0,
          borderDash: [],
        }
      ];

      // rebuild chart options with updated labels
      this.buildChartOptions();
    }
  }

  @Input() set month(months: string[]) {
    if (months) {
      this.lineChartLabels = months;
    }
  }

  isChartBlur: boolean = false;
  lineChartLegend: boolean = true;
  lineChartType: string = 'line';
  lineChartData;
  lineChartLabels: Array<any> = [];
  lineChartOptions: any;

  constructor(private translateService: TranslateService) {
    Chart.register(this.shadowPlugin);
  }

  ngOnInit(): void {
    // Initialize translated labels
    this.updateTranslatedLabels();

    // Initialize chart options with default labels
    this.buildChartOptions();

    // Subscribe to language changes to update labels when language changes
    this.translateService.onLangChange.subscribe(() => {
      this.updateTranslatedLabels();
      if (this.lineChartData) {
        this.updateChartLabels();
        this.buildChartOptions();
      }
    });
  }

  private updateTranslatedLabels(): void {
    this.cumulativeTestResultsLabel = this.translateService.instant(this.CUMULATIVE_TEST_RESULTS_KEY);
    this.benchmarkLabel = this.translateService.instant(this.BENCHMARK_KEY);
  }

  private updateChartLabels(): void {
    if (this.lineChartData && this.lineChartData.length >= 2) {
      this.lineChartData[0].label = this.cumulativeTestResultsLabel;
      this.lineChartData[1].label = this.benchmarkLabel;
    }
  }

  private buildChartOptions(): void {
    this.lineChartOptions = {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            font: {
              size: 12,
            },
            padding: 24,
            color: '#000A12',
            borderRadius: 4,
            boxHeight: 12,
            boxWidth: 36,
            useBorderRadius: true,
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              if (context.dataset.label === this.benchmarkLabel) {
                return null;
              }
              return context.dataset.label + ': ' + context.raw;
            },
            title: (context) => {
              if (context[0].dataset.label === this.benchmarkLabel) {
                return null;
              }
              return context[0].label;
            }
          },
        },
      },
      scales: {
        x: {
          type: 'category',
        },
        y: {
          type: 'category',
          labels: this.labels,
          ticks: {
            crossAlign: 'center',
            align: 'center',
            callback: (value) => {
              if (!value) return '';
              const gradeLabels = this.labels;
              return gradeLabels[value];
            },
          },
        }
      },
    };
  }

  shadowPlugin: Plugin = {
    id: 'shadowPlugin',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      chart.data.datasets.forEach((dataset, index) => {
        if (dataset.label === 'Benchmark') {
          const meta = chart.getDatasetMeta(index);
          if (!meta.hidden) {
            ctx.save();

            ctx.shadowColor = '#5DB605';
            ctx.shadowBlur = 15;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;

            ctx.strokeStyle = dataset.borderColor as string;
            ctx.lineWidth = dataset.borderWidth as number;
            ctx.beginPath();
            meta.data.forEach((point, i) => {
              if (i === 0) {
                ctx.moveTo(point.x, point.y);
              } else {
                ctx.lineTo(point.x, point.y);
              }
            });
            ctx.stroke();

            ctx.restore();
          }
        }
      });
    }
  };
}
